/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    MOBILE_TABLET_MIGRATION_PHASE3
 * Date:         2026-02-09
 * Modified By:  Claude
 *
 * Description:
 * Added mobile responsive support with card-based layout for mobile devices.
 *
 * Related Files:
 * - client/src/features/inventory/InventoryTable.tsx
 * - client/src/features/inventory/components/MobileInventoryList.tsx
 */

import React from "react";
import { Table, Badge, Button, Pagination } from "react-bootstrap";
import { InventoryItem } from "../../services/adminInventory";
import { formatDateTime } from "../../utils/dateFormat";
import { useUserCountry } from "../../hooks/useUserCountry";
// [2026-02-13] CURRENCY_PHASE2: Centralized single-price display
import { formatCurrency } from "../../utils/priceDisplay";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { MobileInventoryList } from "./components/MobileInventoryList";

interface InventoryTableProps {
  rows: InventoryItem[];
  total: number;
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
  loading: boolean;
  onSort: (field: string) => void;
  onPageChange: (page: number) => void;
  onView: (item: InventoryItem) => void;
  onEdit: (item: InventoryItem) => void;
  onDelete: (item: InventoryItem) => void;
}

const InventoryTable: React.FC<InventoryTableProps> = ({
  rows,
  total,
  page,
  limit,
  sortBy,
  sortOrder,
  loading,
  onSort,
  onPageChange,
  onView,
  onEdit,
  onDelete,
}) => {
  const totalPages = Math.ceil(total / limit);
  const country = useUserCountry();
  // [2026-02-13] Maya: Show mobile cards for both mobile and tablet
  const { isMobile, isTablet } = useMediaQuery();

  // [2026-02-13] CURRENCY_PHASE2: Single price in user's currency
  const formatPrice = (item: InventoryItem) => {
    return formatCurrency(item.price, item.currency, item.convertedPrice, item.displayCurrency) || "N/A";
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return formatDateTime(dateString, country);
  };

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return "↕️";
    return sortOrder === "asc" ? "↑" : "↓";
  };

  const renderPagination = () => {
    const items = [];
    const startPage = Math.max(1, page - 2);
    const endPage = Math.min(totalPages, page + 2);

    // First page
    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => onPageChange(1)}>
          1
        </Pagination.Item>,
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="ellipsis1" />);
      }
    }

    // Current range
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item key={i} active={i === page} onClick={() => onPageChange(i)}>
          {i}
        </Pagination.Item>,
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis2" />);
      }
      items.push(
        <Pagination.Item key={totalPages} onClick={() => onPageChange(totalPages)}>
          {totalPages}
        </Pagination.Item>,
      );
    }

    return items;
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-muted">No inventory items found.</p>
      </div>
    );
  }

  // [2026-02-13] Maya: Render card-based list on mobile and tablet devices
  if (isMobile || isTablet) {
    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="u-P3-14-medium">
            {(page - 1) * limit + 1}-{Math.min(page * limit, total)} of {total}
          </div>
        </div>

        <MobileInventoryList
          rows={rows}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
          formatPrice={formatPrice}
          formatDate={formatDate}
        />

        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-3">
            <Pagination>
              <Pagination.Prev disabled={page === 1} onClick={() => onPageChange(page - 1)} />
              {renderPagination()}
              <Pagination.Next
                disabled={page === totalPages}
                onClick={() => onPageChange(page + 1)}
              />
            </Pagination>
          </div>
        )}
      </div>
    );
  }

  // Desktop/Tablet: Standard table view
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} items
        </div>
      </div>

      <div className="table-responsive" style={{ width: "100%", overflowX: "auto" }}>
        <Table striped bordered hover style={{ width: "100%", minWidth: "800px" }}>
          <thead>
            <tr>
              <th className="cursor-pointer" onClick={() => onSort("manufacturer")}>
                Manufacturer {getSortIcon("manufacturer")}
              </th>
              <th className="cursor-pointer" onClick={() => onSort("partNumber")}>
                Part #
              </th>
              <th className="cursor-pointer" onClick={() => onSort("model")}>
                Model
              </th>
              <th className="cursor-pointer" onClick={() => onSort("title")}>
                Title
              </th>
              <th className="cursor-pointer" onClick={() => onSort("quantity")}>
                Qty
              </th>
              <th className="cursor-pointer" onClick={() => onSort("condition")}>
                Condition
              </th>
              <th className="cursor-pointer" onClick={() => onSort("price")}>
                Price
              </th>
              <th className="cursor-pointer" onClick={() => onSort("country")}>
                Country
              </th>
              <th className="cursor-pointer" onClick={() => onSort("updatedAt")}>
                Updated
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => (
              <tr key={item._id}>
                <td>{item.manufacturer}</td>
                <td>{item.partNumber || "-"}</td>
                <td>{item.model || "-"}</td>
                <td>{item.title || "-"}</td>
                <td>
                  <Badge bg={item.quantity > 0 ? "success" : "warning"}>{item.quantity}</Badge>
                </td>
                <td>
                  <Badge bg="secondary" className="text-wrap">
                    {item.condition}
                  </Badge>
                </td>
                <td>{formatPrice(item)}</td>
                <td>{item.country || "-"}</td>
                <td>{formatDate(item.updatedAt)}</td>
                <td>
                  <div className="d-flex gap-1">
                    <Button variant="outline-primary" size="sm" onClick={() => onView(item)}>
                      View
                    </Button>
                    <Button variant="outline-warning" size="sm" onClick={() => onEdit(item)}>
                      Edit
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => onDelete(item)}>
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-3">
          <Pagination>
            <Pagination.Prev disabled={page === 1} onClick={() => onPageChange(page - 1)} />
            {renderPagination()}
            <Pagination.Next
              disabled={page === totalPages}
              onClick={() => onPageChange(page + 1)}
            />
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default InventoryTable;
